import type { DashboardBridge } from '../Dashboard/DashboardBridge'
import { getDashboardSurface } from './assistant-actions'

/**
 * A `DashboardBridge` that owns nothing and forwards everything to whichever
 * dashboard is mounted right now.
 *
 * `AssistantProvider` interprets `chatbotMachine` ONCE, at mount, above every
 * route — so `ctx.bridge` cannot be swapped when the user opens a dashboard.
 * Installing this instead of `null` gives the machine a stable object whose
 * TARGET is late-bound: `emitBridgeEvents` keeps its `if (ctx.bridge)` guard
 * satisfied, `dispatchToolToBridge` keeps all ~41 cases unchanged, and the
 * effect lands on the dashboard actually on screen when it fires.
 *
 * With no dashboard registered `emit`/`on`/`onRequest` are no-ops rather than
 * a throw: `emitBridgeEvents` runs inside an xstate action, where a throw
 * would take down the turn rather than report anything useful. `request`
 * REJECTS instead — see the comment on its no-dashboard branch below.
 */
export function createForwardingBridge(): DashboardBridge {
  const target = () => getDashboardSurface()?.bridge ?? null

  return {
    emit(event, payload) {
      target()?.emit(event, payload)
    },
    on(event, listener) {
      return target()?.on(event, listener) ?? (() => {})
    },
    onRequest(kind, handler) {
      return target()?.onRequest(kind, handler) ?? (() => {})
    },
    async request(kind, opts) {
      const bridge = target()

      // Mirrors the real bridge's reject-on-no-handler behaviour (it rejects
      // with a timeout error when nothing answers) rather than inventing a
      // `null` the return type forbids — callers of `request()` already
      // handle a rejection.
      if (!bridge) {
        throw new Error(`DashboardBridge.request("${kind}") — no dashboard is mounted`)
      }

      return await bridge.request(kind, opts)
    },
  }
}
